/**
 * LeadsLineChart.tsx — Daily leads chart using Recharts.
 * Clean separation: data is received via props; no API calls here.
 */
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Area,
  AreaChart,
} from 'recharts';
import { LeadsDayData } from '../../services/salesService';

interface LeadsLineChartProps {
  data: LeadsDayData[];
  loading: boolean;
  error: string | null;
  startDate: string;
  endDate: string;
  onStartDateChange: (d: string) => void;
  onEndDateChange:   (d: string) => void;
}

// ── Custom Tooltip ─────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const count = payload[0]?.value as number;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 shadow text-sm">
      <p className="text-gray-500 dark:text-gray-400">{label}</p>
      <p className="font-bold text-primary-600">
        {count} lead{count !== 1 ? 's' : ''}
      </p>
    </div>
  );
};

// ── Loading skeleton ────────────────────────────────────────────────────────
const ChartSkeleton = () => (
  <div className="animate-pulse space-y-2 py-4">
    {[70, 50, 80, 40, 90, 60, 75].map((h, i) => (
      <div key={i} className="bg-gray-200 dark:bg-gray-700 rounded" style={{ height: h / 4 }} />
    ))}
  </div>
);

// ── Main component (view-only) ───────────────────────────────────────────────
export default function LeadsLineChart({
  data,
  loading,
  error,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: LeadsLineChartProps) {
  const totalLeads = data.reduce((s, d) => s + d.count, 0);
  const maxLeads   = Math.max(...data.map((d) => d.count), 1);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-800 dark:text-white">
            📈 Leads por Dia
          </h2>
          {!loading && !error && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {totalLeads} lead{totalLeads !== 1 ? 's' : ''} no período
            </p>
          )}
        </div>

        {/* Date range filters */}
        <div className="flex gap-2 items-center text-sm flex-wrap">
          <label className="text-gray-500 dark:text-gray-400">De</label>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-gray-700 dark:text-white"
          />
          <label className="text-gray-500 dark:text-gray-400">até</label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      {/* Chart area */}
      {loading && <ChartSkeleton />}

      {!loading && error && (
        <div className="text-center py-8 text-red-500 text-sm">❌ {error}</div>
      )}

      {!loading && !error && data.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm">
          Nenhum lead no período selecionado.
        </div>
      )}

      {!loading && !error && data.length > 0 && (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="leadsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#6366f1" stopOpacity={0}    />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              tickFormatter={(v: string) => {
                // Show dd/MM for compact labels
                const [, mm, dd] = v.split('-');
                return `${dd}/${mm}`;
              }}
              interval="preserveStartEnd"
            />

            <YAxis
              allowDecimals={false}
              domain={[0, maxLeads + 1]}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              width={28}
            />

            <Tooltip content={<CustomTooltip />} />

            <Area
              type="monotone"
              dataKey="count"
              stroke="#6366f1"
              strokeWidth={2.5}
              fill="url(#leadsGradient)"
              dot={{ r: 3, fill: '#6366f1', strokeWidth: 0 }}
              activeDot={{ r: 5 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
